import React, { ChangeEvent, useState } from "react";
import { styled, Body2, fontSizes, hiddenTextCss, media } from "@iventis/styles";
import { FormLabel, TextField } from "@mui/material";

import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { ProgressStep, SelectedStepOption } from "./sign-up-questionnaire";
import TrialQuestionTile from "./trial-question-tile";

interface TrialQuestionFormProps {
    step: ProgressStep;
    onChange: (event: ChangeEvent, choice: SelectedStepOption) => void;
    selectedOption: SelectedStepOption;
}

export const ProjectStep = ({ onChange, step }) => {
    const translate = useIventisTranslate();
    const [isError, setError] = useState(false);
    return (
        <div style={{ maxWidth: "600px", width: "100%", height: "80px" }}>
            <StyledFormLabel>
                <span className="hidden">{translate(Content.login2.questionnaire.projectName.label)}</span>
            </StyledFormLabel>
            <TextField
                style={{ margin: "auto", width: "100%" }}
                variant="outlined"
                onChange={(e) => {
                    if (e.target.value?.length === 0) {
                        setError(true);
                    } else {
                        setError(false);
                    }
                    return onChange(e, { step: step.id, answer: e.target.value });
                }}
                error={isError}
                disabled={false}
                data-testid={step.id}
            />
            {isError && <Body2 style={{ color: "red", textAlign: "left", margin: "auto", width: "100%" }}>*This is a required field</Body2>}
        </div>
    );
};

export const DefaultTiles = ({ stepOption, onChange, step, selectedOption }) => (
    <TrialQuestionTile step={step} stepOption={stepOption} onChange={onChange} checked={selectedOption?.answer === stepOption.id} />
);

const StepViews = {
    "step-4": ProjectStep,
};

export const TrialQuestionForm: React.FC<TrialQuestionFormProps> = ({ step, onChange, selectedOption }) => (
    <>
        <StyledQuestion>{step.content.question}</StyledQuestion>
        {step.content.subLabel && <StyledSubLabel>{step.content.subLabel}</StyledSubLabel>}
        <StyledTileWrapper $step={step}>
            {step.content.options.map((stepOption) => {
                const CurrentStep = StepViews[step.name] ?? DefaultTiles;

                return <CurrentStep key={stepOption.id} step={step} onChange={onChange} selectedOption={selectedOption} stepOption={stepOption} />;
            })}
        </StyledTileWrapper>
    </>
);

const StyledQuestion = styled.h1`
    margin: 20px 16px;
    font-weight: 600;
    text-align: center;
    font-size: ${fontSizes.large};
    min-height: 36px;
    ${media.medium} {
        font-size: ${fontSizes.xxxLarge};
    }
`;

const StyledSubLabel = styled("p")`
    margin: -15px 16px 0 16px;
    color: ${(props) => props.theme.typographyColors.subdued};
    text-align: center;
    font-size: ${fontSizes.small};
    ${media.medium} {
        font-size: ${fontSizes.large};
    }
`;

const StyledTileWrapper = styled("div")<{ $step: ProgressStep }>`
    position: relative;
    padding: 20px;
    height: 100%;
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    flex-wrap: wrap;
    column-gap: 20px;
    row-gap: 20px;

    ${media.medium} {
        column-gap: 50px;
        padding: 20px 40px;
    }
`;

const StyledFormLabel = styled(FormLabel)`
    ${hiddenTextCss};
`;

export default TrialQuestionForm;
